import { Separator } from "@/components/ui/separator";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { JSX, useState, useEffect, useMemo, SetStateAction, Dispatch } from "react";
import { useTranslation } from "react-i18next";
import { RingsLoader } from "../../loaders";
import { useAiImportRuntime } from "@/store/aiImportRuntime";
import { useAiImportStore } from "@/store/aiImports";
import { Button } from "@/components/ui/button";
import { enumToClientText } from "@/utils/helpers";
import { AiImportStatus } from "@/graphql/__generated__/graphql";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useIntl } from "react-intl";
import { TendersWithAiImportsDocument, TendersWithAiImportsQuery, TendersWithAiImportsQueryVariables } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { STEP_KEYS } from "@/utils/aiImport";
import ActiveImportCard from "./ActiveImportCard";
import {
  extractUsedAiImportIds,
  getCompletedImportHistory,
  groupImportHistoryByDay,
  computeImportCalculations,
  getImportsToEnd,
  createViewClickHandler,
  createLocalizedStepContentGetter,
  GroupedHistoryItem,
} from "./utils";
import { formatRelative, Locale } from "date-fns";
import { enGB, de } from "date-fns/locale";
import i18n from "../../../../../i18n";
import { cn } from "@/lib/utils";

// Mapping between i18next language codes and date-fns locales
const localeMap: { [key: string]: Locale } = {
  en: enGB,
  de: de,
};

interface Props {
  setShowAIImportModal: Dispatch<SetStateAction<boolean>>;
}

/**
 * AIImportLoader component
 *
 * This component displays a loader and status information for the current AI import using SSE events.
 *
 * Hooks:
 * - `useNavigate`: React Router hook for navigation.
 * - `useAiImportStore`: Custom hook to access AI import statuses.
 * - `useAIImportStatusPolling`: Custom hook to start polling AI import statuses.
 *
 * States:
 * - `open`: Boolean state to control the visibility of the popover.
 *
 * Functions:
 * - `calculateAveragePercentage`: Calculates the average percentage of all AI import statuses.
 * - `handleViewClick`: Handler for the View button, navigates to the createTender page with the AIImport ID.
 *
 * @returns {JSX.Element} The AIImportLoader component.
 * @component
 */
const AIImportLoader: React.FC<Props> = ({ setShowAIImportModal }: Props): JSX.Element => {

  // Hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { t } = useTranslation(["components", "common"]);

  // Stores
  const { activeImports, endImport, clearSteps } = useAiImportRuntime();

  // States
  const [open, setOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<Record<number, boolean>>({});

  // Locales
  const primaryLanguage = i18n.language.split("-")[0]; // Extract primary language code
  const currentLocale: Locale = localeMap[primaryLanguage] || enGB;

  // Create the localized step content getter function
  const getLocalizedStepContent = useMemo(() =>
    createLocalizedStepContentGetter(t, STEP_KEYS),
  [t]
  );

  // Per-import steps and progress are stored in the runtime store
  const aiImportStatuses = useAiImportStore((state) => state.aiImportStatuses);

  // Fetch tenders with documents that already reference an AI import
  const { data: tendersWithAiImports } = useQuery<TendersWithAiImportsQuery, TendersWithAiImportsQueryVariables>(TendersWithAiImportsDocument,{
    variables: { first: 20 },
    fetchPolicy: "network-only",
  });

  // Build a quick lookup set of aiImportIds already attached to tenders
  const usedAiImportIds = useMemo(() =>
    extractUsedAiImportIds(tendersWithAiImports),
  [tendersWithAiImports]
  );

  // Filter and group completed imports for history view
  const completedHistory = useMemo(() =>
    getCompletedImportHistory(aiImportStatuses, usedAiImportIds),
  [aiImportStatuses, usedAiImportIds]
  );

  const groupedHistory = useMemo((): GroupedHistoryItem[] =>
    groupImportHistoryByDay(completedHistory, intl),
  [completedHistory, intl]
  );

  // Clean up completed imports from active state
  useEffect(() => {
    // Get snapshot of current active imports to avoid feedback loop
    const currentActiveImports = useAiImportRuntime.getState().activeImports;
    const activeIds = Object.keys(currentActiveImports).map(Number);
    if (activeIds.length === 0) return;

    const importsToEnd = getImportsToEnd(aiImportStatuses, activeIds);
    importsToEnd.forEach(id => {
      endImport(id);
      clearSteps(id);
    });
  }, [aiImportStatuses, endImport, clearSteps]);

  // Create the view click handler
  const handleViewClick = useMemo(() =>
    createViewClickHandler(navigate, setOpen),
  [navigate, setOpen]
  );

  // Compute calculations for all active imports
  const importCalculations = useMemo(() =>
    computeImportCalculations(activeImports, aiImportStatuses, getLocalizedStepContent),
  [activeImports, aiImportStatuses, getLocalizedStepContent]
  );

  const { calculations, averageProgress } = importCalculations;
  const activeIds = Object.keys(activeImports).map(Number);
  const numActive = activeIds.length;

  return (
    <Dialog open={open} onOpenChange={setOpen} modal>
      <DialogTrigger data-cy="notification-center-trigger" className="w-auto">
        <div
          className="relative border-x px-2 border-gray-200 hover:border-blue-300 flex items-center gap-2
                    transition-all duration-200 ease-linear active:scale-95 rounded-xs group"
        >
          {/* Spinner loader */}
          {numActive > 0 ? (
            <div className="w-10 h-10 flex items-center-justify">
              <RingsLoader
                visible={true}
                height={40}
                width={40}
                color="#2563EB"
                ariaLabel="rings-loading"
                radius={2}
              />
            </div>
          ) : (
            <SparklesIcon className="w-[20px] h-[20px] text-blue-500" />
          )}
          {/* Status text */}
          <p className="text-gray-500 font-light md:max-[850px]:hidden min-[850px]:flex">
            {
              numActive > 0 ? (
                `${t("aiimportloader.importing")} (${averageProgress}%)`
              ) : (
                t("aiimportloader.manageimports")
              )
            }
          </p>
        </div>
      </DialogTrigger>
      <DialogContent
        className="flex flex-col justify-start min-w-[60vw] max-w-[90vw] lg:max-w-fit max-h-[72vh] h-full md:h-fit select-none gap-0 min-h-0"
      >
        <DialogHeader className="flex flex-col gap-2 h-fit text-start">
          <div className="flex items-center flex-row gap-2">
            {/* Icon */}
            <div className="rounded-full bg-blue-100 p-1 mr-1">
              <SparklesIcon className="w-9 h-9 text-blue-500 animate-pulse p-2" />
            </div>
            <div className="flex flex-col gap-1">
              <DialogTitle>
                {t("aiimportloader.title")}
              </DialogTitle>
              <DialogDescription>
                {t("aiimportloader.description")}
              </DialogDescription>
            </div>
          </div>
          <Separator/>
        </DialogHeader>
        <div className="flex flex-col flex-1 min-h-0 space-y-2 overflow-x-clip mt-2">
          {/* Active imports scroll container */}
          <div className={cn("flex flex-col flex-1", numActive === 0 ? "min-h-20" : "min-h-60")}>
            <div className="mb-2 flex items-center justify-between">
              <Label>
                {t("aiimportloader.activeimports")}
              </Label>

              <div>
                <Button variant="outline" onClick={() => { setOpen(false); setTimeout(() => setShowAIImportModal(true), 0); }}>
                  <SparklesIcon className="w-[16px] h-[16px] text-blue-500" />
                  {t("aiimportloader.newimport")}
                </Button>
              </div>
            </div>
            <Separator />
            <div className="mt-2 overflow-y-auto overflow-x-clip scrollbar-hide flex-1 min-h-20">
              {numActive === 0 ? (
                <div className="text-center h-full items-center justify-center flex min-h-20 flex-1">
                  <p className="text-gray-500 text-sm">
                    {t("aiimportloader.noactiveimports")}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 pb-4 min-h-20">
                  {Object.keys(activeImports).map((idStr) => {
                    const aiImportId = Number(idStr);
                    const calculation = calculations[aiImportId];
                    if (!calculation) return null;

                    const { lastIndex, progress, displaySteps, publicationName: currentPublicationName } = calculation;
                    return (
                      <ActiveImportCard
                        key={aiImportId}
                        progress={progress}
                        lastIndex={lastIndex}
                        aiImportId={aiImportId}
                        displaySteps={displaySteps}
                        setIsExpanded={setIsExpanded}
                        isExpanded={isExpanded[aiImportId]}
                        currentPublicationName={currentPublicationName ?? ""}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Import history scroll container */}
          <div className="flex flex-col flex-1 min-h-0">
            <div className="mb-2 flex items-center justify-between">
              <Label>
                {t("aiimportloader.importhistory")}
              </Label>
            </div>
            <Separator />
            <div className="mt-2 overflow-y-auto overflow-x-clip flex-1 min-h-0 max-h-100">
              {groupedHistory.length === 0 ? (
                <div className="text-center py-8 min-h-20">
                  <p className="text-gray-500 text-sm">
                    {t("aiimportloader.noimports")}
                  </p>
                </div>
              ) : (
                <div className="min-h-20">
                  {groupedHistory.map((group) => (
                    <div key={group.key} className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-[11px] text-gray-500">{group.label}</Label>
                      </div>
                      {group.items.map((aiImport) => {

                        return (
                          <div
                            key={aiImport.id}
                            className="mb-1 relative flex items-center gap-2 group bg-gray-50 w-full h-[50px] rounded-lg st-animate-200 select-none"
                          >
                            <div />
                            <div className="flex items-center justify-between w-full absolute top-2 left-2 text-xs text-gray-800">
                              <div className="flex items-center ">
                                <div className="ml-1">
                                  <p className="font-semibold text-xs text-gray-800 truncate max-w-[220px] md:max-w-[350px] lg:max-w-[400px] xl:max-w-[700px]">
                                    {aiImport.publicationNames[0]}
                                    {aiImport.publicationNames[0].includes(".pdf") ? "" : ".pdf"}
                                  </p>
                                  <div className="flex items-center gap-1">
                                    <p className="text-xs text-gray-500 font-light">
                                      {enumToClientText(aiImport.status) + " "}
                                    </p>
                                    {/* Hyphen */}
                                    -
                                    {/* Relative timestamp */}
                                    <p className="text-xs text-gray-500 font-light">
                                      {formatRelative(new Date(aiImport.createdAt), new Date(), { locale: currentLocale })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              {aiImport.status === AiImportStatus.Success && (
                                <Button
                                  variant="ghost"
                                  onClick={() => handleViewClick(aiImport.id)}
                                  className="flex h-full justify-end items-center mr-4"
                                >
                                  {t("common:buttons.view")}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIImportLoader;
